import * as React from "react";
import { cn } from "@/lib/cn";
import { Icon } from "@/shared";

const SIZES = {
  default: "tw-size-4",
  sm: "tw-size-3",
  lg: "tw-size-5",
};

const ICON_SIZE = {
  default: "tw-size-[14px] tw-text-[14px]/none ",
  sm: "tw-size-[10px] tw-text-[10px]/none ",
  lg: "tw-size-[18px] tw-text-[18px]/none ",
};

interface CheckboxContextType {
  size: keyof typeof SIZES;
  checked: boolean;
  disabled: boolean;
}

const CheckboxContext = React.createContext<CheckboxContextType>({
  size: "default",
  checked: false,
  disabled: false,
});

const Checkbox = ({
  children,
  className,
  size = "default",
  checked = false,
  disabled = false,
  onCheckedChange,
}: {
  children: React.ReactNode;
  checked: boolean;
  className?: string;
  size?: keyof typeof SIZES;
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}) => {
  const handleToggle = () => {
    if (disabled) return;
    onCheckedChange?.(!checked);
  };

  return (
    <CheckboxContext.Provider value={{ size, checked, disabled }}>
      <div
        onClick={handleToggle}
        className={cn(
          "tw-inline-flex tw-cursor-pointer tw-items-center tw-gap-2",
          className,
        )}
      >
        {children}
      </div>
    </CheckboxContext.Provider>
  );
};

const CheckboxToggle = ({ className }: { className?: string }) => {
  const { size, checked, disabled } = React.useContext(CheckboxContext);

  return (
    <div
      role="switch"
      aria-checked={checked}
      className={cn(
        "tw-flex tw-items-center tw-justify-center tw-rounded tw-border tw-border-blue-100 tw-bg-white tw-outline-none tw-transition-colors tw-duration-150",
        SIZES[size],
        checked && "tw-border-primary-base tw-bg-primary-base",
        disabled && "tw-border-blue-100 tw-bg-blue-100",
        className,
      )}
    >
      <Icon
        icon="ri-check-line"
        className={cn(
          "tw-invisible tw-text-white tw-opacity-0",
          ICON_SIZE[size],
          checked && "tw-visible tw-opacity-100",
          disabled && "tw-text-blue-500",
        )}
      />
    </div>
  );
};

const Label = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  const { size } = React.useContext(CheckboxContext);

  return (
    <span
      className={cn(
        "tw-select-none tw-font-sans tw-text-[13px]/normal tw-font-normal tw-text-blue-base",
        size === "lg" && "tw-text-base/normal",
        className,
      )}
    >
      {children}
    </span>
  );
};

Checkbox.Toggle = CheckboxToggle;
Checkbox.Label = Label;

export default Checkbox;
